import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Clock, MagnifyingGlass, MonitorPlay } from "@phosphor-icons/react";

const modalityLabels = { online: "Online", presencial: "Presencial", mixta: "Mixta" };

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [modality, setModality] = useState("all");
  const [duration, setDuration] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/courses/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (modality !== "all") params.modality = modality;
    if (q) params.q = q;
    api.get("/courses", { params }).then((r) => setCourses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, category, modality]);

  const filtered = courses.filter((c) => {
    if (duration === "short") return c.duration_hours <= 16;
    if (duration === "long") return c.duration_hours > 16;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <p className="text-xs uppercase tracking-overline text-primary mb-2 font-mono">Catálogo</p>
      <h1 className="font-display text-4xl font-800 tracking-tight mb-8">Explora nuestros cursos</h1>

      <div className="grid md:grid-cols-4 gap-3 mb-8 bg-white border border-border rounded-lg p-4">
        <div className="relative md:col-span-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar curso..."
            className="pl-9" data-testid="catalog-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="catalog-filter-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todas las categorías</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={modality} onValueChange={setModality}>
          <SelectTrigger data-testid="catalog-filter-modality"><SelectValue placeholder="Modalidad" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todas las modalidades</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="presencial">Presencial</SelectItem>
            <SelectItem value="mixta">Mixta</SelectItem>
          </SelectContent>
        </Select>
        <Select value={duration} onValueChange={setDuration}>
          <SelectTrigger data-testid="catalog-filter-duration"><SelectValue placeholder="Duración" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Cualquier duración</SelectItem>
            <SelectItem value="short">Hasta 16 horas</SelectItem>
            <SelectItem value="long">Más de 16 horas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Cargando cursos...</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground" data-testid="catalog-empty">No se encontraron cursos con esos filtros.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
          {filtered.map((c, i) => (
            <Link key={c.id} to={`/curso/${c.id}`} data-testid={`catalog-course-${c.id}`}
              className="group bg-white border border-border rounded-lg overflow-hidden transition-transform hover:-translate-y-[3px] hover:shadow-md animate-fade-up"
              style={{ animationDelay: `${i * 40}ms` }}>
              <div className="aspect-video overflow-hidden bg-secondary relative">
                <img src={c.image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-xs font-medium px-2 py-1 rounded flex items-center gap-1">
                  <MonitorPlay size={13} /> {modalityLabels[c.modality]}
                </span>
              </div>
              <div className="p-5">
                <span className="text-xs font-mono text-primary uppercase tracking-wide">{c.category}</span>
                <h3 className="font-display font-700 text-lg tracking-tight mt-1 leading-snug line-clamp-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.description}</p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-3">
                  <span className="flex items-center gap-1"><Clock size={15} /> {c.duration_hours}h</span>
                  <span className="flex items-center gap-1"><Star size={15} weight="fill" className="text-accent" /> {c.rating}</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="font-display font-800 text-xl">{formatCLP(c.price)}</span>
                  <span className="text-sm font-medium text-primary group-hover:underline">Ver curso</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
